import { DialogId, DialogSchema, StepId } from '@feedyou/schema'
import memoizeOne from 'memoize-one'

import { searchStep } from '../../../components/steps/utils/stepServices/stepServices'
import { createConfigs, searchMutableObject } from './searchMutableObject'
import { memoizeByRefAndId } from '../../../lib/performance/memoizeByRefAndId'

export type OccurrenceType = 'storage' | 'text' | 'dialog' | 'id' | 'name'

export type FoundEntity = {
  id: string
  type: 'step' | 'dialog' | 'answer' | 'tool' | 'carousel' | 'switch_case'
}

export type FoundEntityLocation = {
  dialog: DialogId
  step: StepId | null
}

// "partial" can be derived from within the searched entity itself,
// "full" depends on the search context, and needs to be injected later.
export type FoundOccurrence<T extends 'full' | 'partial'> = {
  occurrenceType: OccurrenceType
  foundOnEntity: T extends 'full'
    ? FoundEntity & FoundEntityLocation // I <3 PRETTIER
    : FoundEntity
}

export function completeFoundOccurrence(location: FoundEntityLocation) {
  return function completeFoundOccurrence(partial: FoundOccurrence<'partial'>) {
    return { ...partial, foundOnEntity: { ...partial.foundOnEntity, ...location } }
  }
}

export function searchInDialogs(query: string, dialogs: DialogSchema[]): FoundOccurrence<'full'>[] {
  return dialogs.flatMap(dialog => getMemoizedInstanceOfSearchInDialog(query)(dialog))
}

function searchInDialog(dialog: DialogSchema, query: string): FoundOccurrence<'full'>[] {
  const directSearchResult = searchMutableObject(
    dialog,
    query,
    { type: 'dialog', id: dialog.id },
    createConfigs({
      id: ['id'],
      name: ['name']
    })
  ).map(completeFoundOccurrence({ dialog: dialog.id, step: null }))

  const stepResults = dialog.steps.flatMap(step => {
    const foundInStep = searchStep(step, query)
    return foundInStep.map(completeFoundOccurrence({ dialog: dialog.id, step: step.id }))
  })

  return [...directSearchResult, ...stepResults]
}

// This makes it so subsequent edits take no time while search field is populated (all but changed dialogs return cached results)
const getMemoizedInstanceOfSearchInDialog = memoizeOne((query: string) => {
  return memoizeByRefAndId((dialog: DialogSchema) => searchInDialog(dialog, query))
})
